import { Injectable } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { User } from '../user/models/user.model';

@Injectable()
export class ValidationService {
  constructor() {
  }

  public isValidFormControl(formGroup: UntypedFormGroup, formControlName: string): boolean {
    if (!formGroup.controls[formControlName]) {
      console.error(`Invalid form control name: ${formControlName}`);
      return true;
    }

    return !!this.getValidationClass(formGroup, formControlName);
  }

  public getValidationClass(formGroup: UntypedFormGroup, formControlName: string): string {
    if (!formGroup.controls[formControlName]) {
      console.error(`Invalid form control name: ${formControlName}`);
      return '';
    }

    const isDirtyOrTouched = formGroup.controls[formControlName].dirty || formGroup.controls[formControlName].touched;
    if (!isDirtyOrTouched) {
      return '';
    }

    return formGroup.controls[formControlName].errors ? 'invalid' : 'valid';
  }

  public clearFormControlValidators(controls: AbstractControl[], options = {}) {
    controls.forEach(
      (control) => {
        control.clearValidators();
        control.updateValueAndValidity(options);
      },
    );
  }

  public setFormControlValidators(control: AbstractControl, validatorFunction: ValidatorFn, options = {}) {
    control.setValidators(validatorFunction);
    control.updateValueAndValidity(options);
  }

  public setValidatorFormControls(validatorFunction: ValidatorFn, controls: AbstractControl[], options = {}) {
    controls.forEach((control) => {
      control.setValidators(validatorFunction);
      control.updateValueAndValidity(options);
    });
  }

  public getFormControlValidationErrors(controls, source: string) {
    const formValidationErrors = [];

    Object.keys(controls).forEach((key) => {
      const controlErrors: ValidationErrors = controls[key].errors;
      if (controlErrors != null) {
        Object.keys(controlErrors).forEach((keyError) => {
          formValidationErrors.push({
            source,
            keyError,
            keyControl: key,
            errValue: JSON.stringify(controlErrors[keyError]),
          });
        });
      }

      if (controls[key].controls) {
        this.getFormControlValidationErrors(controls[key].controls, source);
      }
    });

    return formValidationErrors;
  }

  //#region FormControl Validators
  public minTimeValidator(timeString: string): ValidatorFn {
    const minDay = this.getMomentFromTime(timeString);

    return (control: AbstractControl) => {
      const controlDay = this.getMomentFromTime(control.value);

      if (controlDay.isBefore(minDay)) {
        return {
          minTime: {
            valid: false,
          },
        };
      }

      return null;
    };
  }

  public maxTimeValidator(timeString: string): ValidatorFn {
    const maxDay = this.getMomentFromTime(timeString);

    return (control: AbstractControl) => {
      const controlDay = this.getMomentFromTime(control.value);

      if (controlDay.isAfter(maxDay)) {
        return {
          maxTime: {
            valid: false,
          },
        };
      }

      return null;
    };
  }

  public betweenTimeValidator(minTime: string, maxTime: string): ValidatorFn {
    const minDay = this.getMomentFromTime(minTime);
    const maxDay = this.getMomentFromTime(maxTime);

    return (control: AbstractControl) => {
      const controlDay = this.getMomentFromTime(control.value);

      if (!controlDay.isBetween(minDay, maxDay, 'minute', '[]')) {
        return {
          betweenTime: {
            valid: false,
          },
        };
      }

      return null;
    };
  }

  public isInteger(): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.value === '' || control.value === null || typeof control.value === 'undefined') {
        return null;
      }

      if (control.value.toString() === parseInt(control.value, 10).toString()) {
        return null;
      }

      return {
        notInteger: {
          valid: false,
        },
      }
    };
  }

  public isExpectedValue(expectedValue: number): ValidatorFn {
    return (control: AbstractControl) => {
      if (control.value === '' || control.value === null || typeof control.value === 'undefined') {
        return null;
      }

      /* if (control.value.toString() === expectedValue.toFixed(20).replace(/\.?0+$/, '')) {
         return null;
       } */
      // MV3-4343 - above code replaced by below block
      if ( +(+control.value).toFixed(2) === +expectedValue.toFixed(2)) {
        return null;
      }

      return {
        expectedValue: {
          valid: false,
        },
      }
    };
  }

  /**
   * Validate a single email address.
   * @returns {ValidatorFn}
   */
  public emailFormatValidator(): ValidatorFn {
    const emailRegExp = new RegExp(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);

    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }

      if (!emailRegExp.test(control.value)) {
        return {
          emailFormat: {
            valid: false,
          },
        };
      }

      return null;
    };
  }

  /**
   * Validate multiple email addresses separated by a comma or a semicolon.
   * @returns {ValidatorFn}
   */
  public emailsFormatValidator(): ValidatorFn {
    const emailRegExp = new RegExp(/^\s*(([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)(\s*(;|,)\s*|\s*$))*$/);

    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }

      if (!emailRegExp.test(control.value)) {
        return {
          emailFormat: {
            valid: false,
          },
        };
      }

      return null;
    };
  }

  /**
   * Asynchronously validate multiple email addresses separated by a comma or a semicolon.
   * @returns {ValidatorFn}
   */
  public emailsFormatValidatorAsync(): AsyncValidatorFn {
    const emailRegExp = new RegExp(/^\s*(([a-zA-Z0-9_\-\.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9\-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)(\s*(;|,)\s*|\s*$))*$/);

    return (control: AbstractControl): Observable<{ [key: string]: { valid: boolean } } | null> => {
      if (!control.value) {
        return of(null);
      }

      if (!emailRegExp.test(control.value)) {
        return of({
          emailFormat: {
            valid: false,
          },
        });
      }

      return of(null);
    };
  }

  public booleanValidator(value: boolean, expectedValue: boolean): ValidatorFn {
    return (control: AbstractControl) => {
      if (value === expectedValue) {
        return null;
      }

      return {
        expect: expectedValue,
      };
    };
  }

  public mrnFormatValidator(): ValidatorFn {
    // const euCountryCodes = EUCountries.map(country => '(' + country.alpha2Code + ')').join('|');
    const euCountryCodes = '(DE)|(GB)';
    const mrnRegExp = new RegExp('^[0-9a-z]{2}(' + euCountryCodes + ')[0-9a-z]{14}$', 'i');

    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }

      if (!mrnRegExp.test(control.value)) {
        return {
          mrnFormat: {
            valid: false,
          },
        };
      }

      return null;
    };
  }

  public containsAlphabet(): ValidatorFn {
    const alphabetRegExp = /[a-zA-Z]/;

    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }

      if (!alphabetRegExp.test(control.value)) {
        return {
          alphabet: {
            valid: false,
          },
        };
      }

      return null;
    }
  }

  public alphaNumeric(): ValidatorFn {
    const alphabetNumericRegExp = /^[a-zA-Z0-9_ ]*$/;

    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }

      if (!alphabetNumericRegExp.test(control.value)) {
        return {
          notAlphaNumeric: {
            valid: false,
          },
        };
      }

      return null;
    }
  }

  public containsNumber(): ValidatorFn {
    const numberRegExp = /\d+/;

    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }

      if (!numberRegExp.test(control.value)) {
        return {
          number: {
            valid: false,
          },
        };
      }

      return null;
    }
  }

  public compareControlValidator(controlToValidate: string): ValidatorFn {

    return (control: AbstractControl) => {
      if (!control.value) {
        return null;
      }
      if (!control.parent.controls[controlToValidate]) return null;

      if (control.value !== control.parent.controls[controlToValidate].value) {
        return {
          notEqual: {
            valid: false,
          },
        };
      }

      return null;
    }

  }

  public isMatching(controlName: string, matchingControlName: string) {
    return (formGroup: UntypedFormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (matchingControl.errors && !matchingControl.errors.mustMatch) {
        // return if another validator has already found an error on the matchingControl
        return;
      }

      // set error on matchingControl if validation fails
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
      } else {
        matchingControl.setErrors(null);
      }
    }
  }

  //#endregion

  private getMomentFromTime(timeString: string) {
    const timeFragments = timeString.split(/:/);
    const hour = parseInt(timeFragments[0], 10);
    const minute = parseInt(timeFragments[1], 10);
    return moment('1970-01-01', 'YYYY-MM-DD').hours(hour).minutes(minute).seconds(0);
  }
}
